'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import BigHeader from './BigHeader';
import { CurvedDivider } from './CurvedDivider';
import type { RichTextField } from '@prismicio/client';
import type React from 'react';
import { asText } from '@prismicio/helpers';
import BackgroundBeams from './ui/background-beams';

/**
 * Minimal shape this component consumes. Kept decoupled from any generated
 * document/slice type so it can be fed either by the implementations document
 * (`page.data`) or by the `vertical_features` slice's mapped primary.
 */
interface StepItem {
  title: string | null;
  description: RichTextField;
}

export interface VerticalTabsData {
  getting_started_super_title?: string | null;
  getting_started_title?: string | null;
  getting_started_description?: string | null;
  getting_started_step_1_label?: string | null;
  getting_started_step_1_items?: StepItem[];
  getting_started_step_2_label?: string | null;
  getting_started_step_2_items?: StepItem[];
  getting_started_step_3_label?: string | null;
  getting_started_step_3_items?: StepItem[];
  getting_started_step_4_label?: string | null;
  getting_started_step_4_items?: StepItem[];
}

interface Feature {
  title: string;
  Feature: React.FC<ExperienceFeatureProps>;
  items: StepItem[];
}

interface VerticalSlideFeaturesProps {
  data: VerticalTabsData;
}

const VerticalSlideFeatures: React.FC<VerticalSlideFeaturesProps> = ({ data }) => {
  const [selected, setSelected] = useState<number>(0);

  const FEATURES: Feature[] = [
    {
      title: data.getting_started_step_1_label || '',
      Feature: ExperienceFeature,
      items: data.getting_started_step_1_items || [],
    },
    {
      title: data.getting_started_step_2_label || '',
      Feature: ExperienceFeature,
      items: data.getting_started_step_2_items || [],
    },
    {
      title: data.getting_started_step_3_label || '',
      Feature: ExperienceFeature,
      items: data.getting_started_step_3_items || [],
    },
    {
      title: data.getting_started_step_4_label || '',
      Feature: ExperienceFeature,
      items: data.getting_started_step_4_items || [],
    },
  ];

  return (
    <section className="relative bg-white">
      <CurvedDivider top={true} color="#f3f4f6" />
      <BackgroundBeams opacity={0.02} />
      <div className="sm:py-18 max-w-7xl py-14 pb-24 sm:mx-auto sm:px-4 lg:py-20">
        <div className="container">
          <BigHeader
            superHeader={data.getting_started_super_title || ''}
            title={data.getting_started_title || ''}
            description={data.getting_started_description || ''}
            className="fade-up mb-16 flex flex-col items-center text-center"
          />
          <div className="fade-up flex flex-col-reverse items-center gap-6 sm:py-4 md:flex-row md:gap-12">
            <AnimatePresence mode="wait">
              {FEATURES.map((tab, index) => {
                return selected === index ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    key={index}
                    className="w-full"
                  >
                    <tab.Feature items={tab.items} />
                  </motion.div>
                ) : undefined;
              })}
            </AnimatePresence>
            <Tabs selected={selected} setSelected={setSelected} features={FEATURES} />
          </div>
        </div>
      </div>
    </section>
  );
};

interface TabsProps {
  selected: number;
  setSelected: React.Dispatch<React.SetStateAction<number>>;
  features: Feature[];
}

const Tabs: React.FC<TabsProps> = ({ selected, setSelected, features }) => {
  return (
    <div className="w-full shrink-0 overflow-scroll md:w-fit">
      {features.map((tab, index) => {
        return (
          <Tab
            key={index}
            setSelected={setSelected}
            selected={selected === index}
            title={tab.title}
            tabNum={index}
          />
        );
      })}
    </div>
  );
};

interface TabProps {
  selected: boolean;
  title: string;
  setSelected: React.Dispatch<React.SetStateAction<number>>;
  tabNum: number;
}

const Tab: React.FC<TabProps> = ({ selected, title, setSelected, tabNum }) => {
  return (
    <div className="group relative w-full md:w-fit">
      <button
        onClick={() => setSelected(tabNum)}
        className="relative z-0 flex w-full border-l-[6px] border-slate-200 p-4 transition-colors group-hover:border-slate-300 md:flex-col md:border-l-8 md:p-6"
      >
        <span
          className={`text-start text-base font-semibold transition-colors sm:text-xl xl:min-w-[350px] xl:max-w-[350px] xl:text-2xl ${
            selected ? 'text-blue-500' : 'text-slate-400 group-hover:text-slate-500'
          }`}
        >
          <span className="">{tabNum + 1}.</span> {title}
        </span>
      </button>
      {selected && (
        <motion.span
          layoutId="vertical-slide-feature-slider"
          className="absolute bottom-0 left-0 top-0 z-10 w-[6px] bg-blue-500 md:w-2"
        />
      )}
    </div>
  );
};

interface ExperienceFeatureProps {
  items: StepItem[];
}

const ExperienceFeature: React.FC<ExperienceFeatureProps> = ({ items }) => (
  <div className="min-h-[740px] w-full">
    <div className="relative h-fit w-full rounded-xl bg-blue-900 shadow-xl">
      <div className="flex w-full gap-1.5 rounded-t-xl bg-blue-950 p-3">
        <div className="h-3 w-3 rounded-full bg-red-500" />
        <div className="h-3 w-3 rounded-full bg-yellow-500" />
        <div className="h-3 w-3 rounded-full bg-blue-500" />
      </div>
      <div className="h-full w-full p-6 text-white sm:p-10">
        <ul>
          {items.map((item, index) => (
            <li key={index} className="mb-6 text-blue-300">
              <strong className="mb-1 inline-block font-semibold text-white sm:mb-1 sm:text-lg sm:font-medium">
                {item.title}
              </strong>
              <br />
              {asText(item.description)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export default VerticalSlideFeatures;
